import {
  type ClassArray,
  type ClassDictionary,
  clsx,
  type ClassValue as ClsxClassValue,
} from "clsx";
import { extendTailwindMerge } from "tailwind-merge";

// tailwind-merge with the default (unprefixed) Tailwind config — the bt: prefix
// was removed (see tailwind.css in @btnet/blueprint-components).
// cacheSize is raised from the 500 default: cn runs in ~570 files and the per-render
// working set thrashed the LRU at 500 (see ado-276236 for profiler measurements).
const twMerge = extendTailwindMerge({ cacheSize: 1000 });

/**
 * `cn`'s accepted input shape. Derived from clsx's `ClassValue` with the
 * dictionary form narrowed from `Record<string, any>` to
 * `Record<string, unknown>`. The narrower `unknown` keeps `any` out of
 * consumer call-site type inference — without it, importing `cn` would
 * trip `@typescript-eslint/no-unsafe-*` rules on unrelated lines in
 * consumer files.
 *
 * Built as: clsx's union minus the dict and array forms (which we're
 * replacing) → add our narrowed dict → add a recursive array case pointing
 * at our local `ClassValue`. The array case has to be re-stated because
 * `Exclude` strips `ClassArray` from the union — arrays in TypeScript
 * satisfy `Record<string, any>` and so get swept out alongside the
 * dictionary.
 */
type ClassValue =
  | Exclude<ClsxClassValue, ClassDictionary | ClassArray>
  | Record<string, unknown>
  | readonly ClassValue[];

/**
 * Tailwind-aware className composer. Combines `clsx`-style arguments
 * (strings, objects, arrays, conditionals) into a single space-separated
 * string, then runs the result through `tailwind-merge` so conflicting
 * utilities resolve predictably — later wins, earlier conflicting
 * utility is dropped.
 *
 * This is the single className composer for the app — use it everywhere
 * classes need to be combined, whether or not Tailwind utilities are involved.
 *
 * @example
 * ```tsx
 * function Button({ className, isActive }: Props) {
 *     return (
 *         <button
 *             className={cn(
 *                 "flex p-4 bg-gray-0",
 *                 "flex p-4 bg-gray-0", {
 *                      "bg-blue-50": isActive
 *                 },
 *                 className
 *             )}
 *         />
 *     );
 * }
 *
 * // Consumer overrides reliably:
 * //   <Button className="p-8 bg-red-50" />
 * // p-4 and bg-gray-0 are dropped; consumer's values win.
 * ```
 */
export const cn = (...inputs: ClassValue[]): string => {
  const composed = clsx(...inputs);
  // A single class (or empty string) can't have conflicts, so the merge
  // is provably a no-op — skip the parse + trie walk.
  return composed.indexOf(" ") < 0 ? composed : twMerge(composed);
};
